import React, { useState, useEffect } from 'react'
import { OpportunityFormPresenter } from './opportunity-form-presenter'
import { opportunitySchema } from '../../lib/utils/schema'
import { list, NewOpportunity, PayoutFrequency, RedemptionPeriod } from '../../lib/types'
import { opportunityServices } from '../../services'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { useNavigate, useParams } from 'react-router-dom'
import {
  createOpportunityInitialValues,
  opportunityInitialValues,
} from './opportunity-initial-values'
import { prepareOpportunityPayloadForApi } from './opportunity-form-cms-fields'
import { convertOpportunityMoneyFieldsFromCents } from './opportunity-form-money'
import {
  isValidFileSize,
  isValidImageType,
  isValidLogoImageType,
  isValidDocType,
  isValidVideoType,
} from '../../lib/utils/helpers'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'

const OpportunityFormContainer = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams()
  const { id, projectSlug } = params
  const [initialValues, setInitialValues] = useState<NewOpportunity>(createOpportunityInitialValues)
  const [sectorOptions, setSectorOptions] = useState<list[]>([])

  useEffect(() => {
    const fetchSectors = async () => {
      const response = await opportunityServices.getSectors()
      if (response?.status === 200 && Array.isArray(response.data?.data)) {
        setSectorOptions(
          response.data.data.map((sector: { id: number; name: string }) => ({
            text: sector.name,
            value: String(sector.id),
          })),
        )
      }
    }
    fetchSectors()
  }, [])

  useEffect(() => {
    const fetchOpportunityById = async () => {
      const data: any = await opportunityServices.getOpportunityById(id as string, true)
      if (data && data.data && data.status === 200) {
        const opportunityData = { ...data.data }

        if (!opportunityData.documents?.length) {
          opportunityData.documents = opportunityInitialValues.documents
        }

        opportunityData.metadata = {
          ...opportunityInitialValues.metadata,
          ...opportunityData.metadata,
          reference_valuation: opportunityData.metadata?.reference_valuation ?? null,
        }
        if (opportunityData.metadata?.company?.website !== undefined) {
          const { ...company } = opportunityData.metadata.company
          opportunityData.metadata.company = company
        }

        // investment_return is not editable here and can be NULL; null fails the yup number check
        opportunityData.investment_return =
          opportunityData.investment_return ?? opportunityInitialValues.investment_return
        opportunityData.opportunity_domain = opportunityData.opportunity_domain ?? ''
        opportunityData.marketing_signup_image = opportunityData.marketing_signup_image ?? ''
        opportunityData.marketing_signup_mobile_image =
          opportunityData.marketing_signup_mobile_image ?? ''
        opportunityData.logo_image = opportunityData.logo_image ?? ''
        opportunityData.logo_wide_image = opportunityData.logo_wide_image ?? ''
        opportunityData.marketing_signup_enabled = opportunityData.marketing_signup_enabled ?? false
        opportunityData.transfer_page_enabled = opportunityData.transfer_page_enabled ?? true
        opportunityData.secondary_trading_enabled =
          opportunityData.secondary_trading_enabled ?? false

        // Transform asset_class_data back to individual fields for form editing
        if (opportunityData.asset_class_data) {
          if (
            opportunityData.asset_class === 'Trade Finance' ||
            opportunityData.asset_class === 'Private Credit'
          ) {
            opportunityData.investment_period_in_days =
              opportunityData.asset_class_data.investment_period_in_days
            opportunityData.interest_charged_by_credibila_to_opportunity_percent =
              opportunityData.asset_class_data.interest_charged_by_credibila_to_opportunity_percent
            opportunityData.expected_spread_percent =
              opportunityData.asset_class_data.expected_spread_percent
            opportunityData.days_from_withdraw_request_to_payment =
              opportunityData.asset_class_data.days_from_withdraw_request_to_payment
            opportunityData.exit_load_percent = opportunityData.asset_class_data.exit_load_percent
            opportunityData.profit_sharing_to_credibila_percent =
              opportunityData.asset_class_data.profit_sharing_to_credibila_percent
            opportunityData.hosting_fee_percent =
              opportunityData.asset_class_data.hosting_fee_percent
            opportunityData.has_lock_in_period = opportunityData.asset_class_data.has_lock_in_period
            opportunityData.lock_in_period_in_days =
              opportunityData.asset_class_data.lock_in_period_in_days
            opportunityData.early_redemption_allowed =
              opportunityData.asset_class_data.early_redemption_allowed
            opportunityData.redemption_notice_in_days =
              opportunityData.asset_class_data.redemption_notice_in_days
            opportunityData.redemption_fee_percent =
              opportunityData.asset_class_data.redemption_fee_percent
            opportunityData.redemption_period = opportunityData.asset_class_data.redemption_period
            opportunityData.payout_frequency = opportunityData.asset_class_data.payout_frequency
            opportunityData.redemption_gate_percent =
              opportunityData.asset_class_data.redemption_gate_percent
          } else if (opportunityData.asset_class === 'Mutual Fund') {
            opportunityData.management_fee_percent =
              opportunityData.asset_class_data.management_fee_percent
            opportunityData.hosting_fee_percent =
              opportunityData.asset_class_data.hosting_fee_percent
            opportunityData.profit_sharing_to_credibila_percent =
              opportunityData.asset_class_data.profit_sharing_to_credibila_percent
          } else if (opportunityData.asset_class === 'Listed Equity') {
            opportunityData.lock_in_period_in_days =
              opportunityData.asset_class_data.lock_in_period_in_days
            opportunityData.hosting_fee_percent =
              opportunityData.asset_class_data.hosting_fee_percent
            opportunityData.profit_sharing_to_credibila_percent =
              opportunityData.asset_class_data.profit_sharing_to_credibila_percent
          }
        }

        setInitialValues(convertOpportunityMoneyFieldsFromCents(opportunityData))
      }
    }
    if (id && projectSlug !== 'undefined') fetchOpportunityById()
    else setInitialValues(createOpportunityInitialValues())
    logCrmEvent(EngagementEvents.VISITED_ADD_OPPORTUNITY_PAGE)
  }, [id, projectSlug])

  const handleSubmit = async (opportunity: NewOpportunity) => {
    try {
      const normalizedOpportunity: NewOpportunity = {
        ...opportunity,
        asset_class: (typeof opportunity.asset_class === 'string'
          ? opportunity.asset_class.trim()
          : opportunity.asset_class) as NewOpportunity['asset_class'],
        investment_return:
          opportunity.investment_return?.toString() === '' ? 0 : opportunity.investment_return ?? 0,
      }

      // Transform asset class specific fields into asset_class_data structure
      const transformedOpportunity = { ...normalizedOpportunity }

      if (
        normalizedOpportunity.asset_class === 'Trade Finance' ||
        normalizedOpportunity.asset_class === 'Private Credit'
      ) {
        transformedOpportunity.asset_class_data = {
          payout_frequency:
            normalizedOpportunity.payout_frequency !== undefined &&
            normalizedOpportunity.payout_frequency !== null
              ? (normalizedOpportunity.payout_frequency as PayoutFrequency)
              : null,
          investment_period_in_days: normalizedOpportunity.investment_period_in_days as
            | number
            | null,
          interest_charged_by_credibila_to_opportunity_percent:
            normalizedOpportunity.interest_charged_by_credibila_to_opportunity_percent as
              | number
              | null,
          expected_spread_percent: normalizedOpportunity.expected_spread_percent as number | null,
          days_from_withdraw_request_to_payment:
            normalizedOpportunity.days_from_withdraw_request_to_payment as number | null,
          exit_load_percent: normalizedOpportunity.exit_load_percent as number | null,
          profit_sharing_to_credibila_percent:
            normalizedOpportunity.profit_sharing_to_credibila_percent as number | null,
          hosting_fee_percent: normalizedOpportunity.hosting_fee_percent as number | null,
          has_lock_in_period: normalizedOpportunity.has_lock_in_period as boolean,
          lock_in_period_in_days: normalizedOpportunity.lock_in_period_in_days as number | null,
          early_redemption_allowed:
            normalizedOpportunity.early_redemption_allowed === true ? true : false,
          redemption_notice_in_days:
            normalizedOpportunity.redemption_notice_in_days !== undefined &&
            normalizedOpportunity.redemption_notice_in_days !== null
              ? (normalizedOpportunity.redemption_notice_in_days as number)
              : null,
          redemption_fee_percent:
            normalizedOpportunity.redemption_fee_percent !== undefined &&
            normalizedOpportunity.redemption_fee_percent !== null
              ? (normalizedOpportunity.redemption_fee_percent as number)
              : null,
          redemption_period:
            normalizedOpportunity.redemption_period !== undefined &&
            normalizedOpportunity.redemption_period !== null
              ? (normalizedOpportunity.redemption_period as RedemptionPeriod)
              : null,
          redemption_gate_percent:
            normalizedOpportunity.redemption_gate_percent !== undefined &&
            normalizedOpportunity.redemption_gate_percent !== null
              ? (normalizedOpportunity.redemption_gate_percent as number)
              : null,
        }
      } else if (normalizedOpportunity.asset_class === 'Mutual Fund') {
        transformedOpportunity.asset_class_data = {
          management_fee_percent: normalizedOpportunity.management_fee_percent as number | null,
          profit_sharing_to_credibila_percent:
            normalizedOpportunity.profit_sharing_to_credibila_percent as number | null,
          has_lock_in_period: normalizedOpportunity.has_lock_in_period as boolean,
          lock_in_period_in_days: normalizedOpportunity.lock_in_period_in_days as number | null,
          hosting_fee_percent: normalizedOpportunity.hosting_fee_percent as number | null,
        }
      } else if (normalizedOpportunity.asset_class === 'Listed Equity') {
        transformedOpportunity.asset_class_data = {
          total_shares: normalizedOpportunity.asset_class_data?.total_shares || null,
          profit_sharing_to_credibila_percent:
            normalizedOpportunity.profit_sharing_to_credibila_percent as number | null,
          hosting_fee_percent: normalizedOpportunity.hosting_fee_percent as number | null,
          has_lock_in_period: normalizedOpportunity.has_lock_in_period as boolean,
          lock_in_period_in_days: normalizedOpportunity.lock_in_period_in_days as number | null,
        }
      } else if (normalizedOpportunity.asset_class === 'Private Equity') {
        transformedOpportunity.asset_class_data = {
          total_shares: normalizedOpportunity.asset_class_data?.total_shares || null,
          profit_sharing_to_credibila_percent:
            normalizedOpportunity.profit_sharing_to_credibila_percent as number | null,
          hosting_fee_percent: normalizedOpportunity.hosting_fee_percent as number | null,
          has_lock_in_period: normalizedOpportunity.has_lock_in_period as boolean,
          lock_in_period_in_days: normalizedOpportunity.lock_in_period_in_days as number | null,
        }
      } else {
        // For other asset classes, ensure asset_class_data is null
        transformedOpportunity.asset_class_data = null
      }

      // Remove individual fields from the top level as they should only be in asset_class_data
      // Trade Finance fields
      delete transformedOpportunity.payout_frequency
      delete transformedOpportunity.investment_period_in_days
      delete transformedOpportunity.interest_charged_by_credibila_to_opportunity_percent
      delete transformedOpportunity.expected_spread_percent
      delete transformedOpportunity.days_from_withdraw_request_to_payment
      delete transformedOpportunity.exit_load_percent
      delete transformedOpportunity.hosting_fee_percent
      delete transformedOpportunity.management_fee_percent
      delete transformedOpportunity.profit_sharing_to_credibila_percent
      delete transformedOpportunity.has_lock_in_period
      delete transformedOpportunity.lock_in_period_in_days
      delete transformedOpportunity.total_shares
      delete transformedOpportunity.early_redemption_allowed
      delete transformedOpportunity.redemption_notice_in_days
      delete transformedOpportunity.redemption_fee_percent
      delete transformedOpportunity.redemption_period
      delete transformedOpportunity.redemption_gate_percent
      delete transformedOpportunity.redemption_period_in_days

      const isCreate = !id && !projectSlug
      const apiPayload = prepareOpportunityPayloadForApi(
        transformedOpportunity,
        isCreate ? 'create' : 'update',
      )

      if (isCreate) {
        const projectData: any = await opportunityServices.createOpportunity(apiPayload)
        if (projectData?.status === 200) {
          dispatch(
            showBanner({
              text: 'Opportunity created successfully',
              variant: 'success-banner',
            }),
          )
          logCrmEvent(EngagementEvents.SAVED_OPPORTUNITY, {
            opportunityId: projectData.data?.id,
            opportunityTitle: projectData.data?.opportunity_title,
          })
          navigate('/opportunities')
        } else {
          dispatch(
            showBanner({
              text: projectData?.data?.message || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      } else {
        const projectData: any = await opportunityServices.updateOpportunity(
          apiPayload,
          id as string,
        )
        if (projectData?.status === 200) {
          dispatch(
            showBanner({
              text: 'Opportunity updated successfully',
              variant: 'success-banner',
            }),
          )
        } else {
          dispatch(
            showBanner({
              text: projectData?.data?.message || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      }
    } catch {
      dispatch(
        showBanner({
          text: 'Something went wrong! Please try again.',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleFileUpload = async (fileData: File, fileType: 'image' | 'logo' | 'doc' | 'video') => {
    const isValidType = () => {
      switch (fileType) {
        case 'doc':
          return isValidDocType(fileData)
        case 'video':
          return isValidVideoType(fileData)
        case 'logo':
          return isValidLogoImageType(fileData)
        case 'image':
        default:
          return isValidImageType(fileData)
      }
    }
    const isValidSize = isValidFileSize(fileData, fileType)
    if (isValidType() && isValidSize) {
      const data: any = await opportunityServices.postUploadFiles(fileData)
      if (data) {
        return data
      } else {
        return { message: 'Could not upload file' }
      }
    } else {
      dispatch(
        showBanner({
          text: 'File size or type is incorrect for field',
          variant: 'error-banner',
        }),
      )
    }
  }
  const handleErrorMessage = (val: string) =>
    dispatch(showBanner({ text: val, variant: 'error-banner' }))

  return (
    <OpportunityFormPresenter
      initialValues={initialValues}
      validationSchema={opportunitySchema}
      handleFormSubmit={handleSubmit}
      handleFileUpload={handleFileUpload}
      handleErrorMessage={handleErrorMessage}
      sectorOptions={sectorOptions}
    />
  )
}

export { OpportunityFormContainer }
